import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, MapPin, Lock, History, Sparkles, Info, Loader2, DollarSign } from 'lucide-react';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PageHeader } from '@/components/PageHeader';
import api from '@/lib/api';

const fmtMoney = (cents) => {
    if (!cents) return '—';
    const n = Math.round(cents / 100);
    return `$${n.toLocaleString()}`;
};

const fmtMonth = (period) => {
    if (!period) return '';
    const [y, m] = period.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

export default function MarketTrendsPage() {
    const [history, setHistory] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [benchmarks, setBenchmarks] = useState(null);
    const [benchmarksLoading, setBenchmarksLoading] = useState(true);
    const [priceBench, setPriceBench] = useState(null);
    const [priceBenchLoading, setPriceBenchLoading] = useState(true);
    const [zipInput, setZipInput] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get('/market/my-history?months=12');
                if (!cancelled) setHistory(r.data);
            } catch (_e) {
                if (!cancelled) setHistory({ by_month: [], total_events: 0 });
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
            try {
                const r = await api.get('/market/benchmarks');
                if (!cancelled) setBenchmarks(r.data);
            } catch (_e) {
                if (!cancelled) setBenchmarks({ access: 'locked', message: 'Unable to load benchmarks.' });
            } finally {
                if (!cancelled) setBenchmarksLoading(false);
            }
            try {
                const r = await api.get('/market/price-benchmarks');
                if (!cancelled) setPriceBench(r.data);
            } catch (_e) {
                if (!cancelled) setPriceBench({ access: 'locked', message: 'Unable to load price benchmarks.' });
            } finally {
                if (!cancelled) setPriceBenchLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const refetchBenchmarks = async (zip) => {
        setBenchmarksLoading(true);
        setPriceBenchLoading(true);
        try {
            const url = zip ? `/market/benchmarks?zip_code=${encodeURIComponent(zip)}` : '/market/benchmarks';
            const r = await api.get(url);
            setBenchmarks(r.data);
        } catch (_e) {
            setBenchmarks({ access: 'locked', message: 'Unable to load benchmarks.' });
        } finally {
            setBenchmarksLoading(false);
        }
        try {
            const url = zip ? `/market/price-benchmarks?zip_code=${encodeURIComponent(zip)}` : '/market/price-benchmarks';
            const r = await api.get(url);
            setPriceBench(r.data);
        } catch (_e) {
            setPriceBench({ access: 'locked', message: 'Unable to load price benchmarks.' });
        } finally {
            setPriceBenchLoading(false);
        }
    };

    return (
        <>
            <PageHeader
                title="Market Trends"
                subtitle="Your historical bidding data and anonymized regional benchmarks."
                testId="market-header"
            />

            {/* Soft-sell lock-in tooltip — appears once user has at least one finalized quote */}
            {!historyLoading && history?.total_events > 0 && (
                <Alert className="mb-4 bg-amber-50 border-amber-200" data-testid="market-lockin-tooltip">
                    <Sparkles className="h-4 w-4 text-amber-700" />
                    <AlertTitle className="text-slate-900">Your data is compounding.</AlertTitle>
                    <AlertDescription className="text-sm text-slate-700">
                        Every finalized quote builds your private pricing history inside QuotesFlow.
                        After a few months, you&apos;ll see month-over-month trends here that don&apos;t exist
                        anywhere else — leaving the platform would mean losing this context.
                    </AlertDescription>
                </Alert>
            )}

            {/* ─────────────────── My History ─────────────────── */}
            <Card data-testid="market-my-history-card">
                <CardHeader>
                    <CardTitle className="font-display flex items-center gap-2">
                        <History className="h-4 w-4 text-slate-700" /> Your bidding history (last 12 months)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {historyLoading ? (
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                        </div>
                    ) : !history || history.total_events === 0 ? (
                        <EmptyHistory />
                    ) : (
                        <HistoryCharts data={history} />
                    )}
                </CardContent>
            </Card>

            {/* ─────────────────── Local Benchmarks (sqft-keyed) ─────────────────── */}
            <Card className="mt-4" data-testid="market-benchmarks-card">
                <CardHeader>
                    <CardTitle className="font-display flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-700" /> Local market benchmarks
                        {benchmarks?.access === 'locked' && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 font-normal ml-2">
                                <Lock className="h-3 w-3 mr-1" /> Crew &amp; Business
                            </Badge>
                        )}
                        <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 font-normal ml-auto text-[10px]">
                            Sqft-keyed · $/sqft analytics
                        </Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {benchmarksLoading ? (
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                        </div>
                    ) : benchmarks?.access === 'locked' ? (
                        <LockedTease message={benchmarks.message} />
                    ) : (
                        <BenchmarksPanel
                            data={benchmarks}
                            zipInput={zipInput}
                            setZipInput={setZipInput}
                            onLookup={() => refetchBenchmarks(zipInput)}
                        />
                    )}
                </CardContent>
            </Card>

            {/* ─────────────────── Price-only Benchmarks (full-capture rollup) ─────────────────── */}
            <Card className="mt-4" data-testid="market-price-benchmarks-card">
                <CardHeader>
                    <CardTitle className="font-display flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-slate-700" /> Local price overview
                        {priceBench?.access === 'locked' && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 font-normal ml-2">
                                <Lock className="h-3 w-3 mr-1" /> Crew &amp; Business
                            </Badge>
                        )}
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-normal ml-auto text-[10px]">
                            100% capture · every finalized quote
                        </Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {priceBenchLoading ? (
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                        </div>
                    ) : priceBench?.access === 'locked' ? (
                        <LockedTease message={priceBench.message} />
                    ) : (
                        <PriceBenchmarksPanel data={priceBench} />
                    )}
                </CardContent>
            </Card>

            {/* ─────────────────── Privacy footnote ─────────────────── */}
            <p className="mt-4 text-xs text-slate-500 flex items-start gap-1.5">
                <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                    Benchmarks aggregate ZIP-3-level pricing across QuotesFlow customers. We never reveal
                    individual quotes from other roofing companies. Buckets only appear once at least 5 quotes
                    from 2+ different contractors exist. Your own quotes are never visible to other customers.
                    Full details in our <Link to="/privacy" className="underline">Privacy Policy</Link>.
                </span>
            </p>
        </>
    );
}


function EmptyHistory() {
    return (
        <div className="py-8 px-4 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 mb-3">
                <TrendingUp className="h-6 w-6 text-slate-500" />
            </div>
            <h3 className="font-display text-base font-semibold text-slate-900">No finalized quotes yet</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
                As soon as you finalize your first quotes, you&apos;ll start building your own private pricing history here.
                Trends compound month over month — after a few months you&apos;ll have data that doesn&apos;t exist anywhere else.
            </p>
            <Button asChild className="mt-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="market-empty-cta">
                <Link to="/app/leads">Find your first lead →</Link>
            </Button>
        </div>
    );
}


function HistoryCharts({ data }) {
    const months = data.by_month || [];
    const chartData = months.map((m) => ({
        month: fmtMonth(m.period),
        median: Math.round((m.median_quote_cents || 0) / 100),
        count: m.count || 0,
        per_sqft: m.median_dollar_per_sqft || 0,
    }));
    const totalQuotes = data.total_events;
    const materialMix = data.material_mix || [];

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Stat label="Quotes (12 mo)" value={totalQuotes} />
                <Stat
                    label="Median quote"
                    value={chartData.length ? fmtMoney(chartData[chartData.length - 1].median * 100) : '—'}
                    sub="latest month"
                />
                <Stat
                    label="Median $/sqft"
                    value={chartData.length && chartData[chartData.length - 1].per_sqft ? `$${chartData[chartData.length - 1].per_sqft.toFixed(2)}` : '—'}
                    sub="latest month"
                />
            </div>

            {chartData.length > 0 && (
                <div data-testid="market-history-chart">
                    <div className="text-sm font-medium text-slate-700 mb-2">Median quote total by month</div>
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                            <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                            <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                            <Tooltip
                                contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E2E8F0' }}
                                formatter={(v) => [`$${v.toLocaleString()}`, 'Median']}
                            />
                            <Line type="monotone" dataKey="median" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            {chartData.length > 0 && (
                <div data-testid="market-history-volume">
                    <div className="text-sm font-medium text-slate-700 mb-2">Quote volume by month</div>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                            <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                            <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E2E8F0' }} />
                            <Bar dataKey="count" fill="#0F172A" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {materialMix.length > 0 && (
                <div data-testid="market-history-materials">
                    <div className="text-sm font-medium text-slate-700 mb-2">Most-quoted materials</div>
                    <div className="flex flex-wrap gap-2">
                        {materialMix.slice(0, 5).map((m, i) => (
                            <Badge key={i} variant="outline" className="text-xs font-normal capitalize">
                                {String(m.material).replaceAll('_', ' ')} <span className="ml-1 text-slate-500">({m.count})</span>
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


function LockedTease({ message }) {
    return (
        <div className="py-8 px-4 text-center" data-testid="market-benchmarks-locked">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 mb-3">
                <Lock className="h-6 w-6 text-amber-700" />
            </div>
            <h3 className="font-display text-base font-semibold text-slate-900">Available on Crew &amp; Business</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
                {message || 'Local market benchmarks show median quote totals and $/sqft trends for your ZIP — anonymized across all roofing companies in QuotesFlow.'}
            </p>
            <p className="mt-2 text-xs text-slate-500 max-w-md mx-auto">
                This is the data acquirers and lenders look for: proof you understand your market better than competitors.
                Crew and Business subscribers see it for every quote they bid.
            </p>
            <Button asChild className="mt-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="market-upgrade-cta">
                <Link to="/app/billing">Upgrade to Crew</Link>
            </Button>
        </div>
    );
}


function BenchmarksPanel({ data, zipInput, setZipInput, onLookup }) {
    const rows = data.rows || [];
    const zip3 = data.zip3;

    const chartData = useMemo(() => {
        // For chart we need rows sorted oldest → newest by period
        const sorted = [...rows].sort((a, b) => (a.period || '').localeCompare(b.period || ''));
        // If we have multiple sqft bands per period, aggregate by period (mean of medians)
        const byPeriod = {};
        sorted.forEach((r) => {
            const p = r.period;
            if (!byPeriod[p]) byPeriod[p] = { totals: [], per_sqft: [], samples: 0 };
            byPeriod[p].totals.push(r.quote_total_cents_p50 || 0);
            if (r.dollar_per_sqft_p50) byPeriod[p].per_sqft.push(r.dollar_per_sqft_p50);
            byPeriod[p].samples += r.sample_size_quotes || 0;
        });
        return Object.entries(byPeriod).map(([p, v]) => ({
            month: fmtMonth(p),
            median: v.totals.length ? Math.round(v.totals.reduce((a, b) => a + b, 0) / v.totals.length / 100) : 0,
            per_sqft: v.per_sqft.length ? +(v.per_sqft.reduce((a, b) => a + b, 0) / v.per_sqft.length).toFixed(2) : 0,
            samples: v.samples,
        }));
    }, [rows]);

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1">
                    <Label className="text-xs">Lookup a specific ZIP</Label>
                    <Input
                        placeholder="e.g. 90210"
                        value={zipInput}
                        onChange={(e) => setZipInput(e.target.value)}
                        maxLength={5}
                        data-testid="market-zip-input"
                    />
                </div>
                <Button onClick={onLookup} variant="outline" data-testid="market-zip-lookup">
                    See benchmarks
                </Button>
            </div>

            {zip3 ? (
                <div className="text-sm text-slate-600">
                    Showing area <b>ZIP {zip3}xx</b> — covers ~10–50k households.
                </div>
            ) : (
                <div className="text-sm text-slate-500">No ZIP set yet — enter one above or finalize a quote so we know your area.</div>
            )}

            {rows.length === 0 ? (
                <div className="py-8 px-4 text-center bg-slate-50 rounded-lg" data-testid="market-benchmarks-empty">
                    <p className="font-medium text-slate-900">Building local data</p>
                    <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
                        We don&apos;t have enough data for this area yet (we require ≥5 quotes from ≥2 different
                        roofers before showing benchmarks). Check back as more contractors in your area bid.
                    </p>
                </div>
            ) : (
                <>
                    <div data-testid="market-benchmarks-chart">
                        <div className="text-sm font-medium text-slate-700 mb-2">Median quote total — your ZIP area</div>
                        <ResponsiveContainer width="100%" height={240}>
                            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                                <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                                <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E2E8F0' }} formatter={(v) => [`$${v.toLocaleString()}`, 'Local median']} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Line type="monotone" dataKey="median" name="Median quote" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="border rounded-lg overflow-x-auto" data-testid="market-benchmarks-table">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="p-3 text-left font-medium">Month</th>
                                    <th className="p-3 text-left font-medium">Sqft band</th>
                                    <th className="p-3 text-right font-medium">p10</th>
                                    <th className="p-3 text-right font-medium">Median</th>
                                    <th className="p-3 text-right font-medium">p90</th>
                                    <th className="p-3 text-right font-medium">$/sqft (median)</th>
                                    <th className="p-3 text-right font-medium">Sample</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={i} className="border-t">
                                        <td className="p-3">{fmtMonth(r.period)}</td>
                                        <td className="p-3 capitalize">{r.roof_sqft_band}</td>
                                        <td className="p-3 text-right tabular-nums">{fmtMoney(r.quote_total_cents_p10)}</td>
                                        <td className="p-3 text-right tabular-nums font-medium">{fmtMoney(r.quote_total_cents_p50)}</td>
                                        <td className="p-3 text-right tabular-nums">{fmtMoney(r.quote_total_cents_p90)}</td>
                                        <td className="p-3 text-right tabular-nums">{r.dollar_per_sqft_p50 ? `$${r.dollar_per_sqft_p50}` : '—'}</td>
                                        <td className="p-3 text-right text-slate-500 text-xs">{r.sample_size_quotes} ({r.sample_size_orgs} orgs)</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <p className="text-xs text-slate-500">{data.note}</p>
        </div>
    );
}


function Stat({ label, value, sub }) {
    return (
        <div className="rounded-lg border bg-white p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-1 text-2xl font-display font-semibold text-slate-900 tabular-nums">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}


function PriceBenchmarksPanel({ data }) {
    const rows = data.rows || [];
    const zip3 = data.zip3;

    // Aggregate rows across all periods into one row per price_band so the user
    // sees the distribution of quote totals in their area at a glance.
    const byBand = useMemo(() => {
        const grouped = {};
        rows.forEach((r) => {
            const band = r.price_band;
            if (!grouped[band]) {
                grouped[band] = {
                    band,
                    samples: 0,
                    orgs: new Set(),
                    p10: [], p50: [], p90: [],
                    sqftCoverage: [],
                    materials: {},
                };
            }
            grouped[band].samples += r.sample_size_quotes || 0;
            grouped[band].orgs.add(`${r.zip3}-${r.period}-orgs-${r.sample_size_orgs}`); // crude org dedup approximation
            grouped[band].p10.push(r.quote_total_cents_p10);
            grouped[band].p50.push(r.quote_total_cents_p50);
            grouped[band].p90.push(r.quote_total_cents_p90);
            if (r.sqft_coverage_pct != null) grouped[band].sqftCoverage.push(r.sqft_coverage_pct);
            (r.material_mix || []).forEach((m) => {
                grouped[band].materials[m.material] = (grouped[band].materials[m.material] || 0) + m.pct;
            });
        });
        const order = ['<5k', '5k-8k', '8k-12k', '12k-16k', '16k-20k', '20k-30k', '30k-50k', '50k+'];
        return Object.values(grouped)
            .map((g) => ({
                ...g,
                p10_avg: g.p10.length ? Math.round(g.p10.reduce((a, b) => a + b, 0) / g.p10.length) : 0,
                p50_avg: g.p50.length ? Math.round(g.p50.reduce((a, b) => a + b, 0) / g.p50.length) : 0,
                p90_avg: g.p90.length ? Math.round(g.p90.reduce((a, b) => a + b, 0) / g.p90.length) : 0,
                sqft_pct: g.sqftCoverage.length ? Math.round(g.sqftCoverage.reduce((a, b) => a + b, 0) / g.sqftCoverage.length) : 0,
                topMaterial: Object.entries(g.materials).sort((a, b) => b[1] - a[1])[0]?.[0] || null,
            }))
            .sort((a, b) => order.indexOf(a.band) - order.indexOf(b.band));
    }, [rows]);

    const totalQuotes = rows.reduce((acc, r) => acc + (r.sample_size_quotes || 0), 0);

    return (
        <div className="space-y-4">
            <div className="text-sm text-slate-600">
                {zip3 ? (
                    <>This is the full pricing distribution for area <b>ZIP {zip3}xx</b> — every finalized quote contributes, including manually-priced ones.</>
                ) : (
                    <>Finalize a quote or set a ZIP above to see the local price distribution.</>
                )}
            </div>

            {rows.length === 0 ? (
                <div className="py-8 px-4 text-center bg-slate-50 rounded-lg" data-testid="market-price-benchmarks-empty">
                    <p className="font-medium text-slate-900">Building local price data</p>
                    <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
                        We don&apos;t have enough finalized quotes in this area yet (we require ≥5 quotes from ≥2 different
                        roofers before showing benchmarks). This view captures every finalized quote — even manual ones — so
                        coverage grows fastest here.
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Stat label="Quotes (12 mo)" value={totalQuotes.toLocaleString()} sub="in your ZIP3 area" />
                        <Stat label="Price bands seen" value={byBand.length} sub="of 8 possible" />
                        <Stat
                            label="Most common band"
                            value={byBand.length ? byBand.reduce((a, b) => (a.samples > b.samples ? a : b)).band : '—'}
                            sub="by quote volume"
                        />
                    </div>

                    <div data-testid="market-price-benchmarks-chart">
                        <div className="text-sm font-medium text-slate-700 mb-2">Median quote by price band</div>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={byBand.map((b) => ({ band: b.band, median: Math.round(b.p50_avg / 100), samples: b.samples }))} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                                <XAxis dataKey="band" stroke="#64748B" fontSize={12} />
                                <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E2E8F0' }}
                                    formatter={(v, n) => n === 'median' ? [`$${v.toLocaleString()}`, 'Median'] : [v, n]}
                                />
                                <Bar dataKey="median" fill="#10B981" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="border rounded-lg overflow-x-auto" data-testid="market-price-benchmarks-table">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="p-3 text-left font-medium">Price band</th>
                                    <th className="p-3 text-right font-medium">p10</th>
                                    <th className="p-3 text-right font-medium">Median</th>
                                    <th className="p-3 text-right font-medium">p90</th>
                                    <th className="p-3 text-right font-medium">Quotes</th>
                                    <th className="p-3 text-left font-medium">Top material</th>
                                    <th className="p-3 text-right font-medium">w/ sqft</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byBand.map((b, i) => (
                                    <tr key={i} className="border-t">
                                        <td className="p-3 font-medium">{b.band}</td>
                                        <td className="p-3 text-right tabular-nums">{fmtMoney(b.p10_avg)}</td>
                                        <td className="p-3 text-right tabular-nums font-medium">{fmtMoney(b.p50_avg)}</td>
                                        <td className="p-3 text-right tabular-nums">{fmtMoney(b.p90_avg)}</td>
                                        <td className="p-3 text-right tabular-nums text-slate-700">{b.samples}</td>
                                        <td className="p-3 text-xs capitalize text-slate-600">{b.topMaterial ? b.topMaterial.replaceAll('_', ' ') : '—'}</td>
                                        <td className="p-3 text-right text-xs text-slate-500">{b.sqft_pct}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <p className="text-xs text-slate-500">{data.note}</p>
        </div>
    );
}
